import type { AxiosInstance } from 'axios';
import axios from 'axios';

class LogoutAbortController {
    private logoutController = new AbortController();
    private readonly axiosInstances = new Set<AxiosInstance>();

    constructor() {
        this.registerAxiosInstance(axios); // add default axios instance
    }

    private mergeWithLogoutSignal(existing: AbortSignal | undefined): AbortSignal {
        if (!existing) return this.logoutController.signal;
        if (existing === this.logoutController.signal) return this.logoutController.signal;
        if (existing.aborted || this.logoutController.signal.aborted) {
            const preAbortedController = new AbortController();
            preAbortedController.abort();
            return preAbortedController.signal;
        }
        if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.any === 'function') {
            return AbortSignal.any([existing, this.logoutController.signal]);
        }
        const controller = new AbortController();
        const onAbort = () => controller.abort();
        existing.addEventListener('abort', onAbort, { once: true });
        this.logoutController.signal.addEventListener('abort', onAbort, { once: true });
        return controller.signal;
    }

    private registerAxiosInstance(instance: AxiosInstance): void {
        if (this.axiosInstances.has(instance)) return;
        this.axiosInstances.add(instance);
        instance.interceptors.request.use((config) => {
            config.signal = this.mergeWithLogoutSignal(config.signal as AbortSignal | undefined);
            return config;
        });
    }

    cancelPendingRequests(): void {
        this.logoutController.abort();
        this.logoutController = new AbortController();
    }

    addAxiosInstance(instance: AxiosInstance): void;
    addAxiosInstance(instances: AxiosInstance[]): void;
    addAxiosInstance(instanceOrInstances: AxiosInstance | AxiosInstance[]): void {
        const instances = Array.isArray(instanceOrInstances) ? instanceOrInstances : [instanceOrInstances];
        instances.forEach((instance) => this.registerAxiosInstance(instance));
    }
}

export const logoutAbortController = new LogoutAbortController();
